import { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import OutroAndMusicControls from "./OutroAndMusicControls";
import ReelOverlayControls from "./ReelOverlayControls";
import CinematicAndCaptionsControls from "./CinematicAndCaptionsControls";
import AspectExportControls from "./AspectExportControls";

/**
 * Timeline editor — stitch already-generated clips into a reel.
 * Final-Cut-Lite: no regeneration, no Magnific cost.
 */
export default function ClipTimeline({ listingId, defaultTitle, onCompiled }) {
  const [available, setAvailable] = useState([]);
  const [hasOverflow, setHasOverflow] = useState(false);
  const availableScrollRef = useRef(null);
  const [timeline, setTimeline] = useState([]); // [{uid, job_id, video_url, title, duration, image_url}]
  const [title, setTitle] = useState(defaultTitle || "");
  const [voEnabled, setVoEnabled] = useState(false);
  const [voText, setVoText] = useState("");
  const [voiceId, setVoiceId] = useState("21m00Tcm4Tlmv3GHvAln");
  const [submitting, setSubmitting] = useState(false);
  const [outroEnabled, setOutroEnabled] = useState(false);
  const [presentedByEnabled, setPresentedByEnabled] = useState(false);
  const [introCardEnabled, setIntroCardEnabled] = useState(false);
  const [transitionStyle, setTransitionStyle] = useState("crossfade");
  const [transitionDuration, setTransitionDuration] = useState(0.6);
  const [transitionOptions, setTransitionOptions] = useState([]);
  const [overlays, setOverlays] = useState({});
  const [musicTrackId, setMusicTrackId] = useState(null);
  // Cinematic options — new
  const [cinematicEnabled, setCinematicEnabled] = useState(false);
  const [cinematicMaterial, setCinematicMaterial] = useState("gold");
  const [cinematicPlacement, setCinematicPlacement] = useState("sky");
  const [cinematicText, setCinematicText] = useState("");
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionsStyle, setCaptionsStyle] = useState("bold_white");
  const [logoIntroEnabled, setLogoIntroEnabled] = useState(false);
  const [logoIntroStyle, setLogoIntroStyle] = useState("glint_sweep");
  const [extraAspects, setExtraAspects] = useState([]);
  const previewAudio = useRef(null);

  const load = async () => {
    if (!listingId) return;
    try {
      const { data } = await api.get(`/jobs?listing_id=${listingId}`);
      setAvailable(data.filter((j) => j.status === "done" && j.video_id));
    } catch {}
  };

  useEffect(() => {
    setTitle(defaultTitle || "");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, defaultTitle]);

  useEffect(() => {
    api
      .get("/transitions")
      .then((r) => setTransitionOptions(r.data || []))
      .catch(() => setTransitionOptions([]));
  }, []);

  // Real overflow detection — only show the "scroll for more" hint if the
  // available-clips panel actually has hidden content below the fold.
  useEffect(() => {
    const measure = () => {
      const el = availableScrollRef.current;
      if (!el) {
        setHasOverflow(false);
        return;
      }
      setHasOverflow(el.scrollHeight - el.clientHeight > 4);
    };
    measure();
    const id = window.setTimeout(measure, 200);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [available]);

  const inTimelineIds = new Set(timeline.map((t) => t.job_id));

  const addClip = (job) => {
    setTimeline((arr) => [
      ...arr,
      {
        uid: crypto.randomUUID(),
        job_id: job.id,
        video_url: mediaUrl(job.video_url),
        image_url: job.image_url ? mediaUrl(job.image_url) : null,
        title: job.template_name || "Custom",
        duration: job.duration,
      },
    ]);
  };

  const removeFromTimeline = (uid) =>
    setTimeline((arr) => arr.filter((x) => x.uid !== uid));

  const move = (uid, dir) => {
    setTimeline((arr) => {
      const i = arr.findIndex((x) => x.uid === uid);
      if (i < 0) return arr;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const previewVoice = async () => {
    if (!voText.trim()) return;
    try {
      const { data } = await api.post(
        "/voiceover/preview",
        { text: voText.trim(), voice_id: voiceId },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      if (previewAudio.current) previewAudio.current.pause();
      const a = new Audio(url);
      previewAudio.current = a;
      a.play();
    } catch {
      toast.error("Voice preview failed");
    }
  };

  const compile = async () => {
    if (timeline.length === 0) {
      toast.error("Drop at least one clip on the timeline");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reels/compile", {
        title: title.trim() || defaultTitle || "Compiled Reel",
        listing_id: listingId || null,
        clip_job_ids: timeline.map((t) => t.job_id),
        voiceover_text: voEnabled && voText.trim() ? voText.trim() : null,
        voice_id: voiceId,
        outro_enabled: outroEnabled,
        intro_card_enabled: introCardEnabled,
        presented_by_enabled: presentedByEnabled,
        transition_style: transitionStyle,
        transition_duration: transitionDuration,
        overlays,
        music_track_id: musicTrackId || null,
        cinematic_text_enabled: cinematicEnabled,
        cinematic_text_opts: cinematicEnabled
          ? {
              material: cinematicMaterial,
              placement: cinematicPlacement,
              duration: 3.0,
              text: cinematicText.trim() || undefined,
            }
          : null,
        captions_enabled: captionsEnabled && voEnabled && voText.trim().length > 0,
        captions_style: captionsStyle,
        logo_intro_enabled: logoIntroEnabled,
        logo_intro_style: logoIntroStyle,
        extra_aspects: extraAspects,
      });
      toast.success("Compiling reel — no regeneration needed");
      onCompiled?.(data);
      setTimeline([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compile failed");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSeconds = timeline.reduce(
    (acc, t) => acc + parseInt(t.duration || "5"),
    0
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Compiled reel title"
          className="flex-1 min-w-[240px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
          data-testid="compile-title-input"
        />
        <div className="text-xs text-zinc-500 font-mono">
          {timeline.length} clips · ~{totalSeconds}s
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-300"
          title="No regeneration — compiled reels cost nothing"
        >
          <Icons.Coins size={11} />
          <span className="text-[10px] font-mono uppercase tracking-wider">
            $0.00 · no regen
          </span>
        </div>
        <button
          onClick={compile}
          disabled={submitting || timeline.length === 0}
          className="bg-white text-black font-medium rounded-lg px-5 py-2 hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-30 transition-all flex items-center gap-2"
          data-testid="compile-generate-button"
        >
          {submitting ? (
            <Icons.Loader2 className="animate-spin" size={14} />
          ) : (
            <Icons.Scissors size={14} />
          )}
          Compile Reel
        </button>
      </div>

      {/* Outro + Music */}
      <OutroAndMusicControls
        outroEnabled={outroEnabled}
        onOutroChange={setOutroEnabled}
        musicTrackId={musicTrackId}
        onMusicChange={setMusicTrackId}
      />

      {/* Cinematic 3D address text + burn-in captions */}
      <CinematicAndCaptionsControls
        cinematicEnabled={cinematicEnabled}
        setCinematicEnabled={setCinematicEnabled}
        cinematicMaterial={cinematicMaterial}
        setCinematicMaterial={setCinematicMaterial}
        cinematicPlacement={cinematicPlacement}
        setCinematicPlacement={setCinematicPlacement}
        cinematicText={cinematicText}
        setCinematicText={setCinematicText}
        captionsEnabled={captionsEnabled}
        setCaptionsEnabled={setCaptionsEnabled}
        captionsStyle={captionsStyle}
        setCaptionsStyle={setCaptionsStyle}
        logoIntroEnabled={logoIntroEnabled}
        setLogoIntroEnabled={setLogoIntroEnabled}
        logoIntroStyle={logoIntroStyle}
        setLogoIntroStyle={setLogoIntroStyle}
        voiceoverEnabled={voEnabled && voText.trim().length > 0}
        listingId={listingId}
      />

      <AspectExportControls
        extraAspects={extraAspects}
        setExtraAspects={setExtraAspects}
      />

      {/* Card toggles + transitions */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={introCardEnabled}
              onChange={(e) => setIntroCardEnabled(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded bg-white/5 border border-white/20 checked:bg-white accent-white"
              data-testid="compile-intro-card-toggle"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <Icons.Tag size={12} className="text-amber-400" />
                <div className="text-[11px] tracking-[0.15em] uppercase text-white">
                  Listing stats card
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                3-second card with price, beds, baths, sqft.
              </div>
            </div>
          </label>
        </div>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={presentedByEnabled}
              onChange={(e) => setPresentedByEnabled(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded bg-white/5 border border-white/20 checked:bg-white accent-white"
              data-testid="compile-presented-by-toggle"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <Icons.UserCircle2 size={12} className="text-sky-400" />
                <div className="text-[11px] tracking-[0.15em] uppercase text-white">
                  "Presented by" card
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                Address + realtor headshot + contact details.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icons.Wand2 size={12} className="text-violet-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Transition between clips
          </div>
        </div>
        {transitionOptions.length === 0 ? (
          <div className="text-[11px] text-zinc-600">Loading…</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {transitionOptions.map((t) => {
                const on = transitionStyle === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTransitionStyle(t.id);
                      if (t.default_duration && t.default_duration > 0) {
                        setTransitionDuration(t.default_duration);
                      } else {
                        setTransitionDuration(0);
                      }
                    }}
                    className={`text-xs rounded-lg px-3 py-1.5 border transition-all ${
                      on
                        ? "bg-white text-black border-white"
                        : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                    }`}
                    title={t.blurb}
                    data-testid={`compile-transition-${t.id}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {transitionStyle !== "cut" && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-[11px] text-zinc-500">Duration</span>
                <input
                  type="range"
                  min="0.2"
                  max="1.5"
                  step="0.1"
                  value={transitionDuration}
                  onChange={(e) => setTransitionDuration(Number(e.target.value))}
                  className="flex-1 max-w-[260px] accent-white"
                  data-testid="compile-transition-duration"
                />
                <span className="text-[11px] font-mono text-zinc-400 w-10 text-right">
                  {transitionDuration.toFixed(1)}s
                </span>
              </div>
            )}
            <div className="text-[11px] text-zinc-600 mt-2 leading-relaxed">
              {(transitionOptions.find((t) => t.id === transitionStyle) || {}).blurb}
            </div>
          </>
        )}
      </div>

      {/* Persistent overlays */}
      <ReelOverlayControls
        listingId={listingId}
        value={overlays}
        onChange={setOverlays}
      />

      {/* Voice-over panel */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Icons.Mic size={14} className="text-zinc-500" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Voice Over (optional)
            </div>
          </div>
          <button
            onClick={() => setVoEnabled((v) => !v)}
            className={`relative w-10 h-5 rounded-full transition-all ${
              voEnabled ? "bg-white" : "bg-white/10"
            }`}
            data-testid="compile-vo-toggle"
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                voEnabled
                  ? "translate-x-5 bg-black"
                  : "translate-x-0.5 bg-white/60"
              }`}
            />
          </button>
        </div>
        {voEnabled && (
          <div className="space-y-3">
            <textarea
              value={voText}
              onChange={(e) => setVoText(e.target.value)}
              rows={3}
              placeholder="Welcome to 1247 Lakeshore Drive…"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              data-testid="compile-vo-text"
            />
            <div className="flex items-center gap-3">
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="21m00Tcm4Tlmv3GHvAln">Rachel</option>
                <option value="pNInz6obpgDQGcFmaJgB">Adam</option>
                <option value="EXAVITQu4vr4xnSDxMaL">Bella</option>
                <option value="ErXwobaYiN019PkySvjV">Antoni</option>
                <option value="MF3mGyEYCl7XYWbV9V6O">Elli</option>
              </select>
              <button
                onClick={previewVoice}
                disabled={!voText.trim()}
                className="text-xs text-white border border-white/20 rounded-lg px-3 py-2 hover:bg-white/10 disabled:opacity-30 flex items-center gap-1.5"
              >
                <Icons.Play size={12} /> Preview
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Library + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
        {/* Available clips */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Available Clips
            </div>
            {available.length > 0 && (
              <div
                className="flex items-center gap-1.5 text-[11px] text-white font-mono"
                data-testid="compile-available-count"
              >
                <span className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10">
                  All {available.length}
                </span>
                {hasOverflow && (
                  <span className="text-[10px] text-zinc-500 hidden sm:inline">
                    · scroll for more
                  </span>
                )}
              </div>
            )}
          </div>
          {available.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-lg p-8 text-center">
              <Icons.Film className="mx-auto text-zinc-700" size={24} />
              <div className="text-xs text-zinc-500 mt-2">
                No completed clips yet for this listing.
              </div>
            </div>
          ) : (
            <div
              ref={availableScrollRef}
              className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[70vh] overflow-y-auto pr-1"
            >
              {available.map((j) => {
                const used = inTimelineIds.has(j.id);
                return (
                  <button
                    key={j.id}
                    onClick={() => addClip(j)}
                    className={`relative aspect-video rounded overflow-hidden bg-black border transition-all text-left ${
                      used
                        ? "border-white/10 opacity-40"
                        : "border-white/10 hover:border-white/40 hover:scale-[1.02]"
                    }`}
                    data-testid={`avail-clip-${j.id}`}
                  >
                    <video
                      src={mediaUrl(j.video_url)}
                      poster={j.image_url ? mediaUrl(j.image_url) : undefined}
                      preload="metadata"
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      onMouseEnter={(e) => {
              const p = e.currentTarget.play();
              if (p && typeof p.catch === "function") p.catch(() => {});
            }}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
                      <div className="text-[10px] text-white font-medium truncate">
                        {j.template_name || "Custom"}
                      </div>
                      <div className="text-[9px] text-zinc-400 font-mono">
                        {j.duration}s
                      </div>
                    </div>
                    {!used && (
                      <div className="absolute top-1 right-1 bg-black/80 rounded p-1 opacity-0 group-hover:opacity-100">
                        <Icons.Plus size={10} className="text-white" />
                      </div>
                    )}
                    {used && (
                      <div className="absolute top-1 right-1 bg-emerald-500/90 rounded p-1">
                        <Icons.Check size={10} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icons.Scissors size={12} className="text-zinc-500" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                Timeline
              </div>
            </div>
            <div className="text-[10px] text-zinc-700 font-mono">
              ~{totalSeconds}s
            </div>
          </div>

          {timeline.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-lg p-8 text-center">
              <Icons.LayoutList
                className="mx-auto text-zinc-700"
                size={24}
              />
              <div className="text-xs text-zinc-500 mt-2">
                Click clips on the left to add them in order. No regeneration —
                they stitch directly with ffmpeg.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {timeline.map((t, i) => (
                <div
                  key={t.uid}
                  className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2"
                  data-testid={`timeline-clip-${i}`}
                >
                  <div className="w-7 h-7 shrink-0 rounded bg-white/10 text-xs font-mono flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="w-24 aspect-video rounded overflow-hidden bg-black shrink-0">
                    <video
                      src={t.video_url}
                      poster={t.image_url || undefined}
                      preload="metadata"
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-medium truncate">
                      {t.title}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {t.duration}s
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => move(t.uid, -1)}
                      className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/10"
                      data-testid={`timeline-up-${i}`}
                    >
                      <Icons.ChevronUp size={12} />
                    </button>
                    <button
                      onClick={() => move(t.uid, 1)}
                      className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/10"
                      data-testid={`timeline-down-${i}`}
                    >
                      <Icons.ChevronDown size={12} />
                    </button>
                    <button
                      onClick={() => removeFromTimeline(t.uid)}
                      className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-red-500"
                    >
                      <Icons.X size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
